import { Button } from '@bambi/component-library/src/components/Button/Button';
import { ReloadIcon } from '@radix-ui/react-icons';
import { useCallback, useEffect } from 'react';

import { useExportInvoices } from '../useExportInvoices';
import { useInvoiceBlobContext } from './InvoiceBlobContext';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import type { PatchedInvoicePaymentLink } from 'src/common/external/bambi-api/bambiApi';
import { useInvoicesPaymentLinkPartialUpdateMutation } from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

interface PaymentLinkGeneratePDFInvoiceButtonProps {
  invoiceId: string;
  invoiceNumber: string;
  uploadedInvoiceFile?: string | null;
}
export function PaymentLinkGeneratePDFInvoiceButton({
  invoiceId,
  invoiceNumber,
  uploadedInvoiceFile,
}: PaymentLinkGeneratePDFInvoiceButtonProps) {
  const dispatch = useAppDispatch();
  const { handleExport, isLoading } = useExportInvoices('BLOB', 'paymentLink');
  const invoiceExportBlobUrl = useAppSelector(
    (state) => state.invoice.invoiceExportBlobUrl
  );
  const editingPaymentLinkId = useAppSelector(
    (state) => state.invoice.editingPaymentLinkId
  );

  const {
    blobUrlBlobMap,
    blobUrlUploadedMap,
    setBlobUrlBlobMap,
    setBlobUrlUploadedMap,
  } = useInvoiceBlobContext();
  const [updater] = useInvoicesPaymentLinkPartialUpdateMutation();

  const handleUploadBlob = useCallback(
    async (blobUrl: string, blob: Blob | undefined) => {
      if (!editingPaymentLinkId || !blob) {
        return;
      }
      const fd = new FormData();
      fd.append('invoice_file', blob, `${invoiceId}.pdf`);

      try {
        await updater({
          id: editingPaymentLinkId,
          patchedInvoicePaymentLink: fd as unknown as PatchedInvoicePaymentLink,
        }).unwrap();
        dispatch(
          show({
            type: 'success',
            title: 'Uploaded invoice pdf to payment link',
          })
        );
        setBlobUrlBlobMap((current) => {
          return {
            ...current,
            [blobUrl]: undefined,
          };
        });
        setBlobUrlUploadedMap((current) => {
          return {
            ...current,
            [blobUrl]: true,
          };
        });
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Failed to upload invoice to payment link',
            description: formatServerError(e),
          })
        );
      }
    },
    [
      editingPaymentLinkId,
      invoiceId,
      updater,
      dispatch,
      setBlobUrlBlobMap,
      setBlobUrlUploadedMap,
    ]
  );

  // Trigger upload after blob has been generated
  useEffect(() => {
    if (!invoiceExportBlobUrl) {
      return;
    }

    const blobUploaded = blobUrlUploadedMap[invoiceExportBlobUrl];
    if (blobUploaded) {
      return;
    }
    const blob = blobUrlBlobMap[invoiceExportBlobUrl];

    handleUploadBlob(invoiceExportBlobUrl, blob);
  }, [
    invoiceExportBlobUrl,
    blobUrlBlobMap,
    blobUrlUploadedMap,
    handleUploadBlob,
  ]);

  return uploadedInvoiceFile ? (
    <div className="flex flex-row">
      <a
        className="border-none py-2 px-3 text-sm text-blue-600 underline"
        href={uploadedInvoiceFile}
        target="__blank"
        rel="noopener noreferrer"
      >
        View Attached Invoice
      </a>
      <Button
        loading={isLoading}
        onClick={() => handleExport('condensed', [invoiceId])}
        variant="icon"
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <ReloadIcon aria-label="Regenerate Invoice" />
          Regenerate
        </div>
      </Button>
    </div>
  ) : (
    <Button
      loading={isLoading}
      onClick={() => handleExport('condensed', [invoiceId])}
    >
      Generate Invoice as PDF to Share
    </Button>
  );
}
